/**
 * Andamento ao vivo do pedido (D13): só pergunta com o painel ABERTO, a cada
 * `REPERGUNTA_ANDAMENTO_MS`, a partir do token da leitura anterior — ninguém
 * lê log de pedido que ninguém está olhando. As linhas já vêm filtradas pelo
 * servidor (`filtro_do_andamento.py`); `useQuery` não acumula páginas por
 * token, então o laço de leitura é manual por isso.
 *
 * O painel zera quando o `stream` muda (plan → replan → apply troca o
 * `task_arn`, e um `nextForwardToken` de um stream não vale para outro) OU
 * quando `reiniciado` vem `true` (o servidor descartou o token e releu do
 * começo do MESMO stream) — nunca pelo rótulo `execucao`, que pode repetir
 * entre disparos (o replan roda na família `tf-apply`, igual ao apply). A
 * comparação do `stream` mora num `useRef` fora do updater de `setState`:
 * nenhum `setState` aqui provoca efeito colateral (zerar outro estado,
 * mutar ref) dentro do próprio callback de atualização.
 *
 * Rodada 1 (C1): o efeito reroda a cada troca de `aberto` (fechar → abrir de
 * novo é o MESMO efeito, com `depois=null`) — o servidor relê o stream desde
 * o início e devolve as mesmas linhas. Por isso `linhas` zera JUNTO com
 * `token.current`/`streamAnterior.current`, no mesmo ponto: sem isso, reabrir
 * durante a mesma execução duplicava a tela.
 *
 * Rodada 1 (C2): `r.disponivel` (o fim da janela de 10 min depois da
 * execução, `leitor_do_andamento.andamento_disponivel`) tem de ser lido.
 * Quando vem `false`, o servidor também manda `stream: null` — isso NÃO é
 * troca de execução (a leitura de `zera` é pulada inteira), as linhas já
 * mostradas ficam como estão, e o laço para de reagendar o próprio timer:
 * sem isso, o painel aberto depois do fim da janela perguntava para sempre
 * E apagava o que já tinha mostrado (stream real → `null` parecia troca).
 *
 * Rodada 2 (M2, revisão final): o laço não tinha `catch` — toda falha HTTP
 * virava unhandled rejection, e o `finally` reagendava de qualquer jeito.
 * Sessão expirada (401) ou pedido que deixou de ser visível
 * (403/404) perguntavam a cada `REPERGUNTA_ANDAMENTO_MS` para sempre. Esses
 * três casos param o laço e viram `encerrado` (tratado como indisponível,
 * mantendo as linhas já lidas — mesmo texto de M1); qualquer outro erro
 * (5xx, rede) é transitório e o `finally` segue reagendando no intervalo
 * normal, igual ao 502 do deploy.
 */
import { useEffect, useRef, useState } from 'react'
import { isAxiosError } from 'axios'
import { getAndamentoTerraform, type AndamentoDoPedido } from '@/api/terraformRunner'

export const REPERGUNTA_ANDAMENTO_MS = 3000
const LINHA_DO_INIT = 'Preparando o ambiente (init)'

export function juntarAndamento(atual: string[], novas: string[]): string[] {
  const jaTemInit = atual.includes(LINHA_DO_INIT)
  return [...atual, ...novas.filter((l) => !(jaTemInit && l === LINHA_DO_INIT))]
}

export interface AndamentoDoPedidoEstado {
  linhas: string[]
  execucao: string | null
  situacao: AndamentoDoPedido['situacao'] | null
  lidoEm: Date | null
  /** C2/M2: a janela de 10 min do backend fechou (`r.disponivel === false`)
   * OU a leitura falhou com 401/403/404 — o painel não pergunta mais, e as
   * linhas acima (se houver) são as últimas lidas. */
  encerrado: boolean
  /** O fim veio de 401/403/404, não da janela: a execução pode seguir
   * rodando, então o painel não pode dizer que ela terminou. */
  leituraRecusada: boolean
}

export function useAndamentoDoPedido(
  pedidoId: number, aberto: boolean, viaChamado?: number | null,
): AndamentoDoPedidoEstado {
  const [linhas, setLinhas] = useState<string[]>([])
  const [execucao, setExecucao] = useState<string | null>(null)
  const [situacao, setSituacao] = useState<AndamentoDoPedido['situacao'] | null>(null)
  const [lidoEm, setLidoEm] = useState<Date | null>(null)
  const [encerrado, setEncerrado] = useState(false)
  const [leituraRecusada, setLeituraRecusada] = useState(false)
  // Estado do laço, não de tela: nada aqui é lido para renderizar, então vive
  // em ref (fora do ciclo de setState) — é o que permite comparar o `stream`
  // ANTES de decidir se este `setLinhas` acumula ou zera.
  const token = useRef<string | null>(null)
  const streamAnterior = useRef<string | null>(null)

  useEffect(() => {
    if (!aberto) return
    let vivo = true
    let timer: ReturnType<typeof setTimeout> | undefined
    token.current = null
    streamAnterior.current = null
    setLinhas([])
    setEncerrado(false)
    setLeituraRecusada(false)

    const ler = async () => {
      let continuarPerguntando = true
      try {
        const r = await getAndamentoTerraform(pedidoId, token.current, viaChamado)
        if (!vivo) return
        if (!r.disponivel) {
          // Fim da janela: `stream: null` aqui não é troca de execução — não
          // toca `streamAnterior`/`linhas`, só avisa e para o laço.
          setEncerrado(true)
          continuarPerguntando = false
          return
        }
        const zera = r.reiniciado
          || (streamAnterior.current !== null && r.stream !== streamAnterior.current)
        streamAnterior.current = r.stream
        token.current = r.proximo
        setLinhas((atual) => juntarAndamento(zera ? [] : atual, r.linhas))
        setExecucao(r.execucao)
        setSituacao(r.situacao)
        setLidoEm(new Date())
      } catch (erro) {
        if (!vivo) return
        // M2: sessão expirada ou pedido que deixou de ser visível — para o
        // laço e trata como indisponível, sem apagar o que já foi lido.
        // Qualquer outro erro (5xx, rede) é transitório: cai no `finally` e
        // repergunta no intervalo normal, sem tocar o estado.
        if (isAxiosError(erro) && [401, 403, 404].includes(erro.response?.status ?? 0)) {
          setEncerrado(true)
          setLeituraRecusada(true)
          continuarPerguntando = false
        }
      } finally {
        if (vivo && continuarPerguntando) timer = setTimeout(ler, REPERGUNTA_ANDAMENTO_MS)
      }
    }
    void ler()
    return () => {
      vivo = false
      if (timer) clearTimeout(timer)
    }
  }, [pedidoId, aberto, viaChamado])

  return { linhas, execucao, situacao, lidoEm, encerrado, leituraRecusada }
}
